<template>
    <div
        class="single-question-wrapper"
        :key="stateId"
        draggable="true"
        @dragstart="startDrag($event, index)"
        @drop="onDrop($event, index)"
        @dragover.prevent
        @dragenter.prevent
    >
        <div class="single-question-extensions">
            <MessageDelaySlider v-model="value.delay" />
        </div>
        <div class="single-question">
            <div class="control-buttons">
                <md-button class="md-icon-button md-mini" @click="() => onRemoved(value.id)">
                    <md-icon>delete</md-icon>
                </md-button>
                <md-button class="md-icon-button md-mini">
                    <md-icon>swap_horiz</md-icon>
                </md-button>
            </div>
            <div class="message-variants">
                <div class="header-content">
                    <span class="label">{{ $t("chatbotv2.questionVariants") }}</span>
                    <div class="numbers">
                        <div
                            v-for="(_, index) in value.questions"
                            class="number"
                            :class="{ active: index === currentIndex }"
                            :key="'number-' + index"
                            @click="currentIndex = index"
                        >
                            {{ index + 1 }}
                        </div>
                        <div class="number" @click="addNewQuestionVariant">+</div>
                    </div>
                </div>
                <div class="message-content" :key="stateId + '-' + currentIndex">
                    <div class="message-variants-remove" v-if="value.questions.length > 1">
                        <md-button class="md-icon-button md-mini" @click="deleteQuestionVariant(currentIndex)">
                            <md-icon>delete_outline</md-icon>
                        </md-button>
                    </div>
                    <TiptapEditor
                        :placeholder="$t('chatbotv2.enterBotResponseHere')"
                        :show-attributes="true"
                        :show-emoji="true"
                        v-model="value.questions[currentIndex]"
                        :key="stateId + '-' + currentIndex"
                    />
                </div>
            </div>
            <div class="entity-picker-section">
                <span class="sectionTitle">{{ $t("chatbotv2.validateResponseWithEntity") }}</span>
                <a-popover v-model="entityPickerVisible" trigger="click" :overlayClassName="'no-padding'">
                    <div slot="content">
                        <ValidationEntityPicker :default-selected="value.validation" :pick="onValidationEntityPicked" />
                    </div>
                    <div class="entity-input">
                        <div class="entity-input-text-wrapper">
                            <div class="entity-input-placeholder" v-if="$_.isEmpty(value.validation)">
                                {{ $t("chatbotv2.selectReplyType") }}
                            </div>
                            <div class="single-entity-style" v-else :class="{ active: entityPickerVisible }">
                                <span>{{ $t(`chatbotv2.${getValidationEntity(value.validation).value}`) }}</span>
                            </div>
                        </div>
                        <span class="material-icons" v-if="!$_.isEmpty(value.validation)"> expand_more </span>
                    </div>
                </a-popover>
            </div>
            <div class="entity-picker-section" v-if="value.validation === 'regex'">
                <div class="sectionTitle">Regex Girin veya Hazır Seçin</div>
                <a-popover
                    :visible="regexPickerVisible"
                    :overlayClassName="'no-padding'"
                    @visibleChange="(visible) => (regexPickerVisible = false)"
                >
                    <div slot="content">
                        <RegexPicker :default-selected="value.regex" :pick="onRegexPicked" />
                    </div>
                    <div class="entity-input">
                        <div class="entity-input-text-wrapper">
                            <TiptapEditor
                                placeholder="Regex girin"
                                :show-attributes="true"
                                :show-emoji="false"
                                v-model="value.regex"
                                :key="stateId + '-' + currentIndex"
                            />
                        </div>
                        <span class="material-icons" @click="regexPickerVisible = !regexPickerVisible">
                            expand_more
                        </span>
                    </div>
                </a-popover>
            </div>
            <div class="attribute-picker-section">
                <span class="sectionTitle"> {{ $t("chatbotv2.saveResposeToAttribute") }}</span>
                <a-popover v-model="attributePickerVisible" trigger="click" :overlayClassName="'no-padding'">
                    <div slot="content">
                        <AttributePicker :default-selected="value.set" :pick="onAttributePicked" />
                    </div>
                    <div class="attribute-input">
                        <div class="attribute-input-text-wrapper">
                            <div class="attribute-input-placeholder" v-if="$_.isEmpty(value.set)">
                                {{ $t("chatbotv2.chooseAttributeType") }}
                            </div>
                            <div class="single-attribute-style" v-else :class="{ active: attributePickerVisible }">
                                <AttributeIconPreview :attribute="value.set" />
                                <span>
                                    {{
                                        `${value.set}`.startsWith("akinon_") ||
                                        `${value.set}`.startsWith("probel_") ||
                                        Attributes.includes(value.set)
                                            ? $t(`chatbotv2.attr.${value.set}`)
                                            : value.set.startsWith("contact_property_")
                                            ? visitorContactAttributes[value.set.replace("contact_property_", "")]
                                            : value.set
                                    }}
                                </span>
                            </div>
                        </div>
                        <span class="material-icons" v-if="!$_.isEmpty(value.set)"> expand_more </span>
                    </div>
                </a-popover>
            </div>
            <div class="failure-section">
                <span class="sectionTitle">{{ $t("chatbotv2.actionOnFailureInput") }}</span>
                <a-select :value="value.onFailure" @change="(e) => (value.onFailure = e)" class="select-failure-action">
                    <a-select-option value="go_to_next_question" v-if="showNextQuestionAction">
                        {{ $t("chatbotv2.askNextQuestion") }}
                    </a-select-option>
                    <a-select-option value="go_to_success" v-else
                        >{{ $t("chatbotv2.goToSuccessBlock") }}
                    </a-select-option>
                    <a-select-option value="go_to_failure">{{ $t("chatbotv2.goToFailureBlock") }}</a-select-option>
                </a-select>
            </div>
            <div class="retry-section mt-3">
                <div class="d-flex flex-row align-items-center justify-content-between">
                    <span>
                        <span v-if="$i18n.locale === 'tr'"> {{ $t("chatbotv2.retrySectionText") }} </span>
                        <span v-else-if="$i18n.locale === 'en' || $i18n.locale === 'ru'">
                            {{ $t("chatbotv2.retrySectionTextPart") }}
                        </span>
                        <a-popover v-model="retryCountPickerVisible" trigger="click" :overlayClassName="'no-padding'">
                            <div slot="content">
                                <div class="list-wrapper">
                                    <div class="list">
                                        <div class="list-item" @click="changeRetryCount()">
                                            {{ $t("chatbotv2.untilFilled") }}
                                        </div>
                                        <div class="list-item" @click="changeRetryCount('1')">
                                            {{ $t("chatbotv2.time1") }}
                                        </div>
                                        <div class="list-item" @click="changeRetryCount('2')">
                                            {{ $t("chatbotv2.time2") }}
                                        </div>
                                        <div class="list-item" @click="changeRetryCount('3')">
                                            {{ $t("chatbotv2.time3") }}
                                        </div>
                                        <div class="list-item" @click="changeRetryCount('4')">
                                            {{ $t("chatbotv2.time4") }}
                                        </div>
                                        <div class="list-item" @click="changeRetryCount('5')">
                                            {{ $t("chatbotv2.time5") }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <span class="text-primary retry-info">{{
                                !!value.retry && !isNaN(value.retry)
                                    ? $t(`chatbotv2.time${value.retry}`)
                                    : $t(`chatbotv2.untilFilled`)
                            }}</span>
                        </a-popover>
                        <span v-if="$i18n.locale === 'en' || $i18n.locale === 'ru'">{{
                            $t("chatbotv2.retrySectionText")
                        }}</span>
                        <span v-else-if="$i18n.locale === 'tr'">{{ $t("chatbotv2.retrySectionTextPart") }}</span>
                    </span>
                    <span class="d-flex flex-row align-items-center">
                        <span class="material-icons text-primary retry-icons mr-1"> repeat_on </span>
                        <span
                            class="material-icons text-primary retry-icons"
                            v-if="!!value.retry && !isNaN(value.retry)"
                        >
                            filter_{{ `${value.retry}` }}
                        </span>
                        <span class="material-icons text-primary retry-icons" v-else> all_inclusive </span>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import { generateRandomId } from "../../../../../../../utils/session";
import TiptapEditor from "../../common/TiptapEditor";
import AttributePicker from "../../pickers/AttributePicker";
import { EntityTypes } from "../../../../palatteEnums";
import ValidationEntityPicker from "../../pickers/ValidationEntityPicker";
import MessageDelaySlider from "../../common/MessageDelaySlider";
import AttributeIconPreview from "../../common/AttributeIconPreview";
import { Attributes } from "../../../../palatteEnums";
import { mapState } from "vuex";
import RegexPicker from "../../pickers/RegexPicker.vue";
import _ from "lodash";
const log = log2("SingleQuestionEditor");
export default {
    name: "SingleQuestionEditor",
    components: {
        RegexPicker,
        TiptapEditor,
        AttributePicker,
        ValidationEntityPicker,
        MessageDelaySlider,
        AttributeIconPreview,
    },
    props: {
        value: {
            type: Object,
            required: true,
            default: () => {
                return {
                    id: generateRandomId(),
                    delay: 2,
                    questions: ["", ""],
                    validation: null,
                    set: null,
                    onFailure: "go_to_failure",
                    retry: "until_filled",
                };
            },
        },
        onRemoved: {
            type: Function,
            required: true,
            stateId: 0,
        },
        showNextQuestionAction: {
            type: Boolean,
            required: true,
            default: false,
        },
        startDrag: {
            type: Function,
            default: () => {},
        },
        onDrop: {
            type: Function,
            default: () => {},
        },
        payload: {
            type: Array,
            default: () => [],
        },
        index: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            currentIndex: 0,
            stateId: 0,
            entityPickerVisible: false,
            regexPickerVisible: false,
            attributePickerVisible: false,
            retryCountPickerVisible: false,
            newPayload: [],
            Attributes,
            EntityTypes,
        };
    },
    computed: {
        ...mapState({
            visitorContactAttributes: (state) =>
                (state.contactProperty.data || []).reduce((a, b) => {
                    a[b._id] = b.label;
                    return a;
                }, {}),
        }),
    },
    methods: {
        changeRetryCount(retry = "until_filled") {
            this.value.retry = retry;
            this.retryCountPickerVisible = false;
        },
        getValidationEntity(entity) {
            if (entity.startsWith("user_")) {
                return {
                    value: entity,
                    name: entity.replace("user_", ""),
                    description: "Custom entries",
                };
            }
            return Object.keys(EntityTypes)
                .map((key) => EntityTypes[key])
                .find((entityType) => entityType.value === entity);
        },
        onValidationEntityPicked(entity) {
            log("onValidationEntityPicked", entity);
            this.value.validation = entity;
            this.entityPickerVisible = false;
        },
        onRegexPicked(regex) {
            log("onRegexPicked", regex);
            this.value.regex = regex;
            this.regexPickerVisible = false;
            this.value = _.cloneDeep(this.value);
            this.stateId++;
        },
        onAttributePicked(attribute) {
            log("onAttributePicked", attribute);
            this.value.set = attribute;
            this.attributePickerVisible = false;
        },
        addNewQuestionVariant() {
            log("addNewQuestionVariant");
            if (this.value.questions.length < 10) {
                this.stateId++;
                this.value.questions.push("");
                this.currentIndex = this.value.questions.length - 1;
            }
        },
        deleteQuestionVariant(index) {
            log("deleteQuestionVariant", index);
            if (this.currentIndex === index) {
                this.currentIndex = Math.max(0, index - 1);
            }
            this.value.questions.splice(index, 1);
            this.stateId++;
        },
    },
    mounted() {
        log("mounted");
    },
};
</script>

<style lang="scss">
.single-question-wrapper {
    margin-bottom: 20px;
    margin-top: 20px;

    .single-question-extensions {
        margin: 0 30px;
    }

    .single-question {
        background: #fff;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        margin: 0 30px;
        padding: 18px 18px 30px;
        position: relative;

        &:hover {
            .control-buttons {
                opacity: 1;
            }
        }

        .entity-picker-section {
            margin-top: 20px;

            .sectionTitle {
                margin-bottom: 10px;
                margin-top: 15px;
                color: #22272d;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .entity-input {
                align-items: center;
                background: #fff;
                border: 1px solid #c6cdd4;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                height: 43px;
                padding: 15px;
                position: relative;
                transition: all 0.3s;
                user-select: none;
                width: 100%;
                margin-top: 10px;

                &:hover {
                    border: 1px solid #006cff !important;
                }

                .entity-input-text-wrapper {
                    display: flex;
                    flex-grow: 1;
                    width: 0;
                }

                .entity-input-placeholder {
                    color: #9ba6b3;
                    font-size: 15px;
                    line-height: 19px;
                    overflow: hidden !important;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .single-entity-style {
                    background: #ffdc66;
                    cursor: pointer;
                    transition: all 0.3s;
                    border-radius: 10px;
                    box-decoration-break: clone;
                    padding: 0 8px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-weight: 800;

                    &.active {
                        background: #006cff;
                        color: #fff;

                        .material-icons {
                            color: #fff;
                        }
                    }

                    .material-icons {
                        color: #647283;
                        font-size: 18px;
                        margin-right: 3px;
                    }
                }
            }
        }

        .attribute-picker-section {
            margin-top: 20px;

            .sectionTitle {
                margin-bottom: 10px;
                margin-top: 15px;
                color: #22272d;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .attribute-input {
                align-items: center;
                background: #fff;
                border: 1px solid #c6cdd4;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                height: 43px;
                padding: 15px;
                position: relative;
                transition: all 0.3s;
                user-select: none;
                width: 100%;

                &:hover {
                    border: 1px solid #006cff !important;
                }

                .attribute-input-text-wrapper {
                    display: flex;
                    flex-grow: 1;
                    width: 0;
                }

                .attribute-input-placeholder {
                    color: #9ba6b3;
                    font-size: 15px;
                    line-height: 19px;
                    overflow: hidden !important;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .single-attribute-style {
                    background: #ffdc66;
                    cursor: pointer;
                    transition: all 0.3s;
                    border-radius: 10px;
                    box-decoration-break: clone;
                    padding: 0 8px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    font-weight: 800;

                    &.active {
                        background: #006cff;
                        color: #fff;

                        .material-icons {
                            color: #fff;
                        }
                    }

                    .material-icons {
                        color: #647283;
                        font-size: 18px;
                        margin-right: 3px;
                    }
                }
            }
        }

        .failure-section {
            margin-top: 20px;

            .sectionTitle {
                margin-bottom: 10px;
                margin-top: 15px;
                color: #22272d;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .ant-select {
                margin-top: 10px;
                width: 100%;
            }
        }

        .retry-section {
            margin-top: 10px;
            user-select: none;

            .times {
                text-decoration: underline;
                color: #006cff;
                font-weight: bolder;
                cursor: pointer;
            }
        }

        .control-buttons {
            align-items: center;
            display: flex;
            flex-direction: column;
            height: 100%;
            left: -44px;
            opacity: 0;
            position: absolute;
            top: 0;
            transition: opacity 0.3s;
            z-index: 13;

            .md-button {
                background-color: #fff;
                align-items: center;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin-bottom: 7px;
                transition: background-color 0.3s;
                height: 30px;
                width: 30px;
                min-height: 30px !important;
                min-width: 30px !important;

                .md-icon {
                    color: #006cff;
                }
            }
        }
    }

    .control-button {
        align-items: center;
        background: #fff;
        border-radius: 100%;
        box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
        cursor: pointer;
        display: flex;
        height: 28px;
        justify-content: center;
        margin-bottom: 7px;
        transition: background-color 0.3s;
        width: 28px;
        position: absolute;
        right: -11px;
        top: -11px;
        border: none;
        z-index: 9;

        &:hover {
            border: 1px solid #006cff !important;
        }
    }

    .header-content {
        display: flex;
        gap: 5px;
        align-items: center;
    }

    .message-content {
        position: relative;
        min-height: 60px;
        background: #dbe1e8;
        border-radius: 8px;

        &:hover {
            border: 1px solid #006cff !important;

            .message-variants-remove {
                opacity: 1;
            }
        }

        .editable-editor {
            overflow-x: hidden;
            overflow-y: auto;
            background: #dbe1e8;
            border-radius: 8px;

            .ProseMirror {
                height: 100%;
            }

            &.focused {
                border: none !important;
            }

            &:focus {
                border: none !important;
            }

            &:hover {
                border: none !important;
                resize: none;
            }
        }

        .editable-editor-wrapper {
            color: #22272d;
            font-size: 16px;
            width: 100%;
            height: calc(100% - 10px);
            background: #dbe1e8;
            border-radius: 8px;

            .ProseMirror {
                width: 100%;
                height: 100%;

                padding: 5px 5px 0;
            }
        }

        .message-variants-remove {
            position: absolute;
            right: -11px;
            top: -11px;
            opacity: 0;

            .md-button {
                background-color: #fff;
                align-items: center;
                border-radius: 100%;
                box-shadow: 0 1px 4px 0 rgb(0 0 0 / 19%);
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin-bottom: 7px;
                transition: background-color 0.3s;
                height: 30px;
                width: 30px;
                min-height: 30px !important;
                min-width: 30px !important;

                .md-icon {
                    color: #006cff;
                }
            }
        }
    }

    .label {
        color: #475e7a;
        font-size: 16px;
        font-weight: 600;
        line-height: 20px;
        margin-right: 12px;
    }

    .numbers {
        display: flex;
        gap: 2px;

        .number {
            box-shadow: 0 2px 6px 0 rgb(0 0 0 / 14%);
            align-items: center;
            background-color: #fff;
            border-radius: 100%;
            color: #475e7a;
            cursor: pointer;
            display: flex;
            flex-shrink: 0;
            font-size: 16px;
            font-weight: 600;
            height: 24px;
            justify-content: center;
            line-height: 20px;
            margin-bottom: 8px;
            margin-right: 8px;
            padding-bottom: 1px;
            width: 24px;

            &.active {
                background: #006cff;
                color: #fff;
            }
        }
    }

    .retry-icons {
        font-size: 15px;
    }

    .retry-info {
        text-decoration-line: underline;
        cursor: pointer;
    }
}

.ant-popover-inner-content {
    .list-wrapper {
        display: flex;
        flex-direction: column;
        max-height: 99vh;
        max-width: 300px;
        overflow: hidden;

        .list {
            overflow-x: hidden;
            overflow-y: auto;

            .list-item:first-child {
                border-top-left-radius: 4px;
                border-top-right-radius: 4px;
            }

            .list-item {
                border-bottom: 1px solid #dbe1e8;
                align-items: center;
                cursor: pointer;
                display: flex;
                height: 35px;
                padding: 0 16px;
                transition: all 0.3s;
                width: 100%;
                overflow: hidden !important;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-weight: 400;
                color: #22272d;
                font-size: 15px;
                line-height: 19px;

                &:hover {
                    background: #008aff;
                    color: #fff;
                    opacity: 0.8 !important;
                }
            }
        }
    }
}
</style>
